import './OrgsTab.less';

import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import _ from 'lodash';
import { Modal } from 'antd';
import {
  CodeOutlined,
  CloudOutlined,
  ExperimentOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { OrgCredential } from '../../interfaces/organization';
import {
  setAllCredentials,
  setDeletingCredetial,
  setOrganization,
} from '../../app/Slices/organizationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../app/store';
import OrganizationCarousel from '../../components/OrganizationCarousel/OrganizationCarousel';
import { OrgEnvironmentType } from '../../enums/organization';
import { confirmAction } from '../../utils/confirmActionModal';
import {
  deleteCredential,
  fetchCredentials,
  refreshCredentials,
  selectCredential,
  verifyCredential,
} from '../../services/organizationService';
import { Notify } from '../../services/notificationService';
import AddOrganizationDialog from '../../components/AddOrganizationDialog/AddOrganizationDialog';
import { CirraServiceType } from '../../enums/auth';
import { getCredentialsToDisplay } from '../../utils/credentialUtils';
import { RESTRICTED_SUBSCRIPTION_STATUSES } from '../../constants/subscription';
import { SubscriptionStatus } from '../../interfaces/subscription';
import { useSecureToken } from '../../hooks/useSecureToken';
import {
  orgRequestDeduplicator,
  debounce,
} from '../../utils/requestDeduplication';
import { getErrorMessage } from '../../utils/errorUtils';
import {
  fetchToolConfigs,
  updateCredential,
} from '../../app/ActionTypes/organizationActions';
import { useAbility } from '../../hooks/useAbility';
import { Resource } from '../../enums/common';
import { useUserAccess } from '../../hooks/useUserAccess';
import { TeamUser } from '../../interfaces/team';
import { PersonalUser } from '../../interfaces/account';

interface NameProps {
  color: string;
  class: React.ComponentType;
}

const ENVS = [
  OrgEnvironmentType.PRODUCTION,
  OrgEnvironmentType.CUSTOM,
  OrgEnvironmentType.SANDBOX,
  OrgEnvironmentType.DEVELOPER,
  OrgEnvironmentType.SCRATCH,
];
const NAME_PROPS_BY_ENV: Record<OrgEnvironmentType, NameProps> = {
  [OrgEnvironmentType.PRODUCTION]: { color: '#4068EB', class: CloudOutlined },
  [OrgEnvironmentType.CUSTOM]: { color: '#4068EB', class: CloudOutlined },
  [OrgEnvironmentType.SANDBOX]: { color: '#169070', class: ExperimentOutlined },
  [OrgEnvironmentType.DEVELOPER]: { color: '#FAAD14', class: CodeOutlined },
  [OrgEnvironmentType.SCRATCH]: { color: '#FAAD14', class: ToolOutlined },
};

const OrgsTab: React.FC = () => {
  const service = CirraServiceType.MCP;
  const dispatch = useDispatch<AppDispatch>();
  const [loadingOrgId, setLoadingOrgId] = useState<string | null>(null);
  const { currentAccount } = useUserAccess();
  const { currentAbility } = useAbility();

  const [openModal, setOpenModal] = useState(false);
  const [resetOrgAdditionModalTrigger, setResetOrgAdditionModalTrigger] =
    useState(0);
  const [selectedOrgTypeForModal, setSelectedOrgTypeForModal] = useState<
    OrgEnvironmentType | undefined
  >(undefined);
  const selectedOrg = useSelector((state: RootState) => state.organization.org);
  const allCredentials = useSelector(
    (state: RootState) => state.organization.credentials
  );
  const toolConfigs = useSelector(
    (state: RootState) => state.organization.toolConfigs
  );
  const isLoadingToolConfigs = useSelector(
    (state: RootState) => state.organization.isLoadingToolConfigs
  );

  const isAccountSwitching = useSelector(
    (state: RootState) => state.team.isAccountSwitching
  );

  const { subscriptionInfo } = useSelector(
    (state: RootState) => state.subscription
  );
  const subscription = subscriptionInfo?.subscription;

  // Use secure token validation hook
  const { validateAndGetToken } = useSecureToken();

  // Operation lock to prevent concurrent operations
  const operationLockRef = useRef<boolean>(false);

  useEffect(() => {
    const loadToolConfigs = async () => {
      try {
        await dispatch(fetchToolConfigs()).unwrap();
      } catch (error) {
        console.error('Failed to load tool configs:', error);
        const userErrorMsg = getErrorMessage({
          error,
          resourceName: 'tool configuration',
          defaultMessage: 'Failed to load tool configurations',
        });
        Notify.error(userErrorMsg, 5, error);
      }
    };
    if (!isAccountSwitching) {
      loadToolConfigs();
    }
  }, [isAccountSwitching]);

  // Memoize credential sorting for better performance
  const sortedCredentials = useMemo(() => {
    // Get valid credentials and sort them by priority: mcp_selected_at > main_selected_at > selected_at
    // Pass currentAccount.cirra_user_id to filter out shared orgs when user already has a connection
    return getCredentialsToDisplay(
      allCredentials,
      ['mcp_selected_at', 'main_selected_at', 'selected_at'],
      currentAccount?.cirra_user_id
    );
  }, [allCredentials, currentAccount?.cirra_user_id]);

  // Compute set of SF org IDs that already have a shared credential
  // Used to prevent sharing a second credential for the same org
  const sharedOrgIds = useMemo(() => {
    return new Set(
      allCredentials
        .filter((c) => c.is_shared === true)
        .map((c) => c.organization_id)
    );
  }, [allCredentials]);

  // Memoize credentials groups for better performance
  const credentialsGroups = useMemo(() => {
    return ENVS.filter((e) => e !== OrgEnvironmentType.CUSTOM).map((env) => {
      const nameProps = NAME_PROPS_BY_ENV[env] as NameProps;
      const Icon = nameProps.class;
      return {
        type: env,
        name: (
          <div style={{ color: nameProps.color }}>
            <Icon />
            &nbsp;
            {_.capitalize(env)}
          </div>
        ),
        items: sortedCredentials.filter(
          (cred) => cred.environment_type === env
        ),
      };
    });
  }, [sortedCredentials]);

  const manageCredentials = useCallback(() => {
    // Select the most recently selected credential based on the ranking
    const recentCredential =
      sortedCredentials.length > 0 ? sortedCredentials[0] : null;
    dispatch(setOrganization(recentCredential));
  }, [sortedCredentials, dispatch]);

  useEffect(() => {
    manageCredentials();
  }, [manageCredentials]);

  const selectOrg = useCallback(
    async (selectedCred: OrgCredential, sameOrg: boolean) => {
      // Use secure token validation
      const token = await validateAndGetToken();
      if (!token) {
        return; // Error already shown by validateAndGetToken
      }

      try {
        const _credentials = await orgRequestDeduplicator.deduplicate(
          'selectOrg',
          async () => {
            return sameOrg
              ? await fetchCredentials()
              : await selectCredential(selectedCred, service);
          }
        );

        if (!sameOrg) {
          Notify.success('Salesforce organization is selected!');
        }

        dispatch(setAllCredentials(_credentials));
        return _credentials;
      } catch (error) {
        Notify.error(
          (error as Error).message ||
            'Failed to select Salesforce organization. Please try again.',
          5000,
          error
        );
        throw error;
      }
    },
    [validateAndGetToken, service, dispatch]
  );

  // Debounced version to prevent excessive calls
  const debouncedFetchCredentials = useMemo(
    () =>
      debounce(async () => {
        // Use secure token validation
        const token = await validateAndGetToken();
        if (!token) {
          return; // Error already shown by validateAndGetToken
        }

        try {
          const _credentials = await orgRequestDeduplicator.deduplicate(
            'fetchCredentials',
            () => fetchCredentials()
          );

          const hasCredListChanges = _.xor(
            _.map(_credentials, 'id'),
            _.map(allCredentials, 'id')
          );
          if (hasCredListChanges.length) {
            dispatch(setAllCredentials(_credentials));
          }

          return _credentials;
        } catch (error) {
          Notify.error(
            (error as Error).message || 'Failed to fetch organizations',
            5000,
            error
          );
          throw error;
        }
      }, 300),
    [validateAndGetToken, allCredentials, dispatch]
  );

  const handleFetchCredentials = useCallback(() => {
    return debouncedFetchCredentials();
  }, [debouncedFetchCredentials]);

  /**
   * Handles the deletion of an organization credential.
   *
   * This function:
   * 1. Checks if an operation is already in progress
   * 2. Confirms the deletion with the user
   * 3. Deletes the organization credential
   * 4. Updates the organization state
   *
   * @param orgCred The organization credential to delete
   * @returns Void
   */
  const handleDeleteCredential = async (orgCred: OrgCredential) => {
    if (!orgCred) {
      return;
    }
    // Check and acquire lock atomically
    if (operationLockRef.current) {
      return;
    }

    try {
      operationLockRef.current = true;
      setLoadingOrgId(orgCred.id);

      // Show different warning for shared connections
      const isSharedConnection = orgCred.is_shared === true;
      const isSharedByOther =
        isSharedConnection &&
        orgCred.cirra_user_id !== currentAccount?.cirra_user_id;

      let confirmContent: string;
      if (isSharedByOther) {
        const ownerLabel = orgCred.cirra_user_id || 'another team member';
        confirmContent = `You are about to delete a shared connection owned by ${ownerLabel}. This will remove the connection for all members of the team.`;
      } else if (isSharedConnection) {
        confirmContent =
          'Deleting this shared connection will remove it for any team members using it. Are you sure you want to remove this Salesforce Org?';
      } else {
        confirmContent = 'Are you sure you want to remove this Salesforce Org?';
      }

      const confirmedRemove = await confirmAction({
        title: 'Remove Salesforce Org',
        content: confirmContent,
        okText: 'Remove',
        okType: 'danger',
        cancelText: 'Cancel',
      });
      if (!confirmedRemove) {
        return;
      }

      await dispatch(setDeletingCredetial({ [orgCred.id]: true }));
      await deleteCredential(orgCred);
      dispatch(setOrganization(null));
      await handleFetchCredentials();
    } catch (error) {
      Notify.error(
        (error as Error).message || 'Failed to delete organization',
        0,
        error
      );
    } finally {
      setLoadingOrgId(null);
      operationLockRef.current = false;
      await dispatch(setDeletingCredetial({ [orgCred.id]: false }));
    }
  };

  /**
   * Handles changing the share option for an organization credential.
   *
   * This function:
   * 1. Shows a confirmation dialog when making a shared connection private
   * 2. Updates the credential's is_shared status
   * 3. Shows success/error notifications
   *
   * @param org The organization credential to update
   * @param isShared Whether the org should be shared
   */
  const handleChangeShareOption = async (
    org: OrgCredential,
    isShared: boolean
  ) => {
    // Show confirmation when making a shared connection private
    if (!isShared && org.is_shared) {
      const confirmed = await confirmAction({
        title: 'Make Connection Private',
        content:
          'Making this connection private will remove it for any team members using it. Are you sure you want to continue?',
        okText: 'Make Private',
        okType: 'danger',
        cancelText: 'Cancel',
      });
      if (!confirmed) {
        return;
      }
    }

    try {
      await dispatch(
        updateCredential({
          id: org.id,
          is_shared: isShared,
        })
      ).unwrap();
      Notify.success(
        isShared
          ? `Organization was successfully shared with the team`
          : `Organization was successfully made private`
      );
    } catch (e) {
      console.log(`Failed to update share option:`, e);
      const errorMessage = getShareErrorMessage(e, isShared);
      Notify.error(errorMessage, 5, e);
    }
  };

  /**
   * Extract a user-friendly error message for share/unshare operations
   */
  const getShareErrorMessage = (error: unknown, isShared: boolean): string => {
    // Redux Toolkit unwrap() throws serialized ActionError with statusCode/message at top level
    const err = error as {
      statusCode?: number;
      message?: string;
    };
    const status = err?.statusCode;
    const apiMessage = err?.message;

    // Duplicate key error - another shared credential exists for this org
    if (status === 409 || apiMessage?.includes('duplicate')) {
      return 'A shared connection for this organization already exists. Please refresh to see the latest connections.';
    }

    // 403 - Insufficient permissions
    if (status === 403) {
      return `You do not have permission to ${isShared ? 'share' : 'unshare'} this organization.`;
    }

    // 400 - Validation error
    if (status === 400 && apiMessage) {
      if (apiMessage.includes('clone')) {
        return 'Cannot share a cloned connection. Please unlink it first.';
      }
      return apiMessage;
    }

    return `Failed to ${isShared ? 'share' : 'unshare'} organization. Please try again.`;
  };

  const selectOrReauthorizeOrg = useCallback(
    async ({ cred, sameOrg }: { cred: OrgCredential; sameOrg: boolean }) => {
      // Note: Lock should already be acquired by the caller
      const cirraUserId = currentAccount?.cirra_user_id || null;

      // Check if this is a shared credential the user doesn't own
      const isSharedCredentialNotOwned =
        cred.cirra_user_id !== cirraUserId && cred.is_shared;

      // If user doesn't own this credential, find their clone or force OAuth
      if (isSharedCredentialNotOwned) {
        try {
          // Look for an existing clone of this shared credential
          const existingClone = allCredentials.find(
            (c) => c.source_id === cred.id && c.cirra_user_id === cirraUserId
          );

          if (existingClone) {
            // Validate that the clone is actually owned by the current user
            if (existingClone.cirra_user_id !== cirraUserId) {
              console.error(
                `Clone ${existingClone.id} found but not owned by current user ${cirraUserId}`
              );
              Notify.error(
                'Invalid credential state. Please refresh the page.'
              );
              return false;
            }

            // User has a clone - use it instead
            const isCloneActive = await verifyCredential(existingClone);
            if (isCloneActive) {
              // Always pass sameOrg=false because we're selecting a different credential (clone vs shared)
              // Note: selectOrg shows its own success notification
              await selectOrg(existingClone, false);
              return false;
            }
            // Clone exists but is expired - reauthorize the clone
            const refreshResult = await refreshCredentials(
              existingClone,
              service
            );
            if (refreshResult.success) {
              // Fetch fresh credentials to update the state
              const freshCredentials = await fetchCredentials();
              dispatch(setAllCredentials(freshCredentials));

              // Use the credentialId returned from OAuth if available
              const cloneId = refreshResult.credentialId || existingClone.id;
              const updatedClone = freshCredentials.find(
                (c) => c.id === cloneId
              );

              if (updatedClone) {
                // Always pass sameOrg=false because we're selecting a different credential (clone vs shared)
                // Note: selectOrg shows its own success notification
                await selectOrg(updatedClone, false);
              } else {
                console.warn(
                  `Clone ${cloneId} not found after reauthorization`
                );
                Notify.error(
                  'Reauthorization succeeded but failed to select the credential. Please contact support.'
                );
                return false;
              }
              return true;
            }
            Notify.warning('Failed to reauthorize Salesforce organization.');
            return false;
          }

          // No clone exists - must go through OAuth to create one
          const refreshResult = await refreshCredentials(cred, service);
          if (refreshResult.success) {
            // Fetch fresh credentials to update the state
            const freshCredentials = await fetchCredentials();
            dispatch(setAllCredentials(freshCredentials));

            // Use the credentialId returned from OAuth - this is the newly created clone
            if (refreshResult.credentialId) {
              const newClone = freshCredentials.find(
                (c) => c.id === refreshResult.credentialId
              );
              if (newClone) {
                // Always pass sameOrg=false because we're selecting a different credential (clone vs shared)
                // Note: selectOrg shows its own success notification
                await selectOrg(newClone, false);
                return true;
              }
            }

            // Fallback: search for clone if credentialId not available (backwards compatibility)
            const newClone = freshCredentials.find(
              (c) =>
                c.source_id === cred.id &&
                c.cirra_user_id === cirraUserId &&
                c.id !== cred.id
            );
            if (newClone) {
              // Note: selectOrg shows its own success notification
              await selectOrg(newClone, false);
              return true;
            }

            // Clone not found
            console.error(
              `Clone not found after OAuth for shared credential ${cred.id}. ` +
                `credentialId from OAuth: ${refreshResult.credentialId}. ` +
                `Looking for source_id=${cred.id}, cirra_user_id=${cirraUserId}. ` +
                `Found credentials:`,
              freshCredentials.map((c) => ({
                id: c.id,
                source_id: c.source_id,
                cirra_user_id: c.cirra_user_id,
                is_shared: c.is_shared,
              }))
            );
            Notify.error(
              'Connection succeeded but failed to select the credential. Please contact support.'
            );
            return false;
          }
          Notify.warning(
            'Failed to connect to Salesforce organization. Please try again.'
          );
          return false;
        } catch (error) {
          Notify.error(
            (error as Error).message ||
              'Failed to connect to shared organization. Please try again.',
            5,
            error
          );
          return false;
        }
      }

      // User owns this credential - proceed with normal flow
      const isActiveOrgUser = await verifyCredential(cred);
      if (isActiveOrgUser) {
        if (!sameOrg) {
          await selectOrg(cred, sameOrg);
        }
        return false;
      }

      try {
        if (!sameOrg) {
          const confirmedRefresh = await confirmAction({
            title: 'Refresh authentication?',
            className: 'sf-org-reauthorize',
            content: (
              <>
                Authentication of{' '}
                <span style={{ whiteSpace: 'nowrap' }}>{cred.username}</span> is
                revoked or expired.
              </>
            ),
            okText: 'Reauthorize',
            cancelText: 'Continue without reauthorizing',
          });
          if (!confirmedRefresh) {
            // For "Continue without reauthorizing", select the org without reauthorizing
            try {
              await selectOrg(cred, sameOrg);
            } catch (error) {
              console.error('Failed to select expired org:', error);
              Notify.error(
                'Failed to select the organization. Please try again.',
                5,
                error
              );
            }
            return false;
          }
        }
        const refreshResult = await refreshCredentials(cred, service);
        if (refreshResult.success) {
          Notify.success(
            `Succcessully ${sameOrg ? 'reauthorized' : 'connected to'} Salesforce organization!`
          );
          await selectOrg(cred, sameOrg);
          return true;
        } else {
          if (sameOrg) {
            Notify.warning('Failed to reauthorize Salesforce organization.');
          } else {
            Notify.warning(
              'Failed to connect to Salesforce organization. Selected org was not changed.'
            );
          }
          return false;
        }
      } catch (error) {
        Notify.error(
          (error as Error).message ||
            'Failed to refresh organization. Selected org was not changed.',
          0,
          error
        );
        throw error;
      }
    },
    [selectOrg, service, currentAccount, allCredentials, dispatch]
  );

  /**
   * Handles the selection or reauthorization of an organization credential.
   *
   * This function:
   * 1. Checks if an operation is already in progress
   * 2. Confirms the selection or reauthorization with the user
   * 3. Selects or reauthorizes the organization credential
   * 4. Updates the organization state
   *
   * @param orgCred The organization credential to select or reauthorize
   * @returns Void
   */
  const handleSelectOrReauthorizeOrg = async (orgCred: OrgCredential) => {
    // Check and acquire lock immediately when button is clicked
    if (operationLockRef.current) {
      return;
    }

    try {
      operationLockRef.current = true;
      setLoadingOrgId(orgCred.id);

      return await selectOrReauthorizeOrg({
        cred: orgCred,
        sameOrg: selectedOrg?.id === orgCred.id,
      });
    } finally {
      // Always release the lock and loading state
      operationLockRef.current = false;
      setLoadingOrgId(null);
    }
  };

  const showModal = (envType?: OrgEnvironmentType) => {
    setSelectedOrgTypeForModal(envType);
    setOpenModal(true);
    setResetOrgAdditionModalTrigger((prev) => prev + 1);
  };

  const handleAddOrganization = (envType?: OrgEnvironmentType) => {
    showModal(envType);
  };

  const getAddDisableStatus = useCallback(
    ({
      envType,
      account,
      // cirraUserId,
      // cirraOrganizationId,
    }: {
      envType: OrgEnvironmentType;
      account: TeamUser | PersonalUser | null | undefined;
    }) => {
      const subscriptionStatus = subscription?.status;
      if (
        subscriptionStatus &&
        RESTRICTED_SUBSCRIPTION_STATUSES.includes(
          subscriptionStatus as SubscriptionStatus
        )
      ) {
        return {
          isAddDisabled: true,
          addDisabledTooltip:
            'Cannot add organizations with current subscription status. Please contact support or update your subscription.',
        };
      }

      if (currentAbility) {
        const tentativeOauthCred = {
          environment_type: envType,
          cirra_user_id: account?.cirra_user_id,
          cirra_organization_id:
            (account as TeamUser)?.cirra_organization_id || null,
        };
        if (
          !currentAbility.can(
            'create',
            Resource.OAuthCredential,
            tentativeOauthCred
          )
        ) {
          return {
            isAddDisabled: true,
            addDisabledTooltip:
              'You do not have permission to add organizations.',
          };
        }
      }

      return { isAddDisabled: false, addDisabledTooltip: '' };
    },
    [subscription, currentAbility]
  );

  return (
    <div className="section section-organizations">
      {!loadingOrgId && !allCredentials?.length && (
        <div className="section-message">
          Please connect a Salesforce organization.
        </div>
      )}

      <div>
        {credentialsGroups.map((group) => {
          const envType = group.type;

          const { isAddDisabled, addDisabledTooltip } = getAddDisableStatus({
            envType,
            account: currentAccount,
          });

          return (
            <OrganizationCarousel
              key={group.type}
              envType={envType}
              title={group.name}
              organizations={group.items}
              selectedOrganization={selectedOrg}
              toolConfigs={toolConfigs}
              isLoadingToolConfigs={isLoadingToolConfigs}
              loadingOrgId={loadingOrgId}
              onAdd={() => {
                handleAddOrganization(group.type);
              }}
              onConnect={handleSelectOrReauthorizeOrg}
              onDelete={handleDeleteCredential}
              onSettings={async () => {
                throw new Error('Unimplemented');
              }}
              onChangeShareOption={handleChangeShareOption}
              addDisabled={isAddDisabled}
              addDisabledTooltip={addDisabledTooltip}
              disabled={!!loadingOrgId}
              sharedOrgIds={sharedOrgIds}
            />
          );
        })}

        <Modal
          title="Add Salesforce Org"
          open={openModal}
          onCancel={() => setOpenModal(false)}
          footer={null}
        >
          <AddOrganizationDialog
            service={service}
            defaultOrgType={selectedOrgTypeForModal}
            onClose={() => setOpenModal(false)}
            resetTrigger={resetOrgAdditionModalTrigger}
            successRedirectTo={null}
            setCredentials={async () => {
              await handleFetchCredentials();
            }}
          />
        </Modal>
      </div>
    </div>
  );
};

export default OrgsTab;
